import { amount, shortDay } from './components/HabitHistory';
import { daysBetween } from './taskVocab';
import type { HabitPolarity } from './api';

/**
 * A habit's figure, said one way on every screen that states it (`01a03813-b97d`).
 *
 * Six lines authored one datum six ways. Measured on one weekly count habit, target 3, nothing
 * logged: the Habits card said *0 of 3 this week*, Today's pill *0/3*, the goal tree *Gym 0/3*, the
 * goal page *0 of 3 this week*, Week *0 / 3* and Reflection *0 / 3 this week* — three grammars, two
 * opinions on whether the unit survives, and three of the six skipping `amount()`, so a week logged
 * as 16.8 + 1.1 read *17.900000000000002 / 18* on Week (`01a15484-9eb4`). And for a ceiling you
 * read with nothing read this period, four of them printed `actual`, the empty period's 0: *Body fat
 * 0 / 14* on Week, *0 % of 14 this day* on the goal page (`01a154cc-c8ac`).
 *
 * The grammar is the Habits card's, chosen by the architect because it is the one that carries
 * the window: **`0 sessions of 3 this week`**. Per case —
 *
 * - **a count** — `3 sessions of 3 this week`, `1 session of 3 this week`; no unit, `0 of 3 this week`.
 * - **a tallied limit** — `2 drinks of 3 allowed this week`. *Allowed*, because a limit's `2 of 3`
 *   is not a shortfall, which is the sentence the strip's hover (`periodFigure`) already says.
 * - **abstinence** — `0 drinks this week`. A limit of zero has nothing to be "of", and *0 of 0*
 *   is not a fact about anything; the verdict beside it says whether it held.
 * - **a reading** — the reading somebody took and, unless it was taken on the day being judged,
 *   its day: `13.2 %`, `13.2 % yesterday`, `13.2 % on 19 Oct` (see *The reading's day* below).
 *   Never `actual`, and never *of* the ceiling, because a
 *   body fat is not a workload half-done. The reading is the service's `lastReading` — the one
 *   `band.toTarget` and the verdict are judged on — so for a weekly `mean` ceiling the row no
 *   longer states the week's mean beside a distance measured from Tuesday's reading. The service
 *   states the value however old the reading is, from the one lookup Week and Reflection read too,
 *   so a reading 100 days old is `13.48 % 3 months ago` on all six (MR !89, review round 1: the
 *   status rows used to drop the value past the 90-day history window and print
 *   `last read 3 months ago` beside Week's `13.48 % 3 months ago`). The `last read …` form is kept
 *   for a payload that carries the day without the value, which this server no longer sends; one
 *   never taken is `no reading yet`.
 *
 * The day is printed whenever the reading is not the day's own, because a figure with no day reads
 * as this morning's. That one rule is what replaces both the root's "this period has no reading"
 * test — which needed `byDay`, a field the goal tree and the review rows do not carry — and the
 * separate *not read for 8 days* clause that followed the verdict on two of the six: the card said
 * *13.48 % on 11 Oct · … · not read for 8 days*, one fact twice in one sentence.
 *
 * ## The reading's day
 *
 * Said as its **distance from the account's today** when the day being judged is today — the
 * Habits card, Today on today, the goal tree and page, the Week and Reflection of the period we
 * are in: `yesterday`, `8 days ago`, `2 weeks ago`, `2 months ago`. Said as the **calendar day**
 * when it is not — Today on a past date, a finished week's Week and Reflection: `on 5 Oct`.
 * (`01a15691-8338`, the architect's ruling.)
 *
 * Distance on today's rows because a past date is an age question, the answer `dueLook` already
 * gives for a task's dates: `8df2009` printed `on 12 Oct` here, and on a 31-row Habits card that
 * is 31 subtractions to find the stale one where `2 weeks ago` is none. The calendar day on a
 * past day's rows because "8 days ago" measured from the end of a finished week is measured from
 * somewhere the reader is not, where `on 5 Oct` on a review of 12–18 Oct plainly says *before
 * this week*. The exact day stays in the expanded card's *Last read on 12 Oct*.
 *
 * The ladder is `386d2b0`'s, whose boundaries were argued and tested there: `yesterday` at one day,
 * days to 13, weeks (whole) from 14 to 59, months (of 30 days) from 60. No period of grace for a
 * weekly reading: `8df2009`'s rule that any reading not the day's own carries its day stands, so
 * a weekly reading seven days old says `7 days ago`. `today` is the account's day, never the
 * browser's or UTC's, which would put every distance one out for most of the day far from Greenwich.
 *
 * ## The window is an argument
 *
 * Never derived from the habit. On Reflection the window is the review's — a daily habit on a week
 * review is judged over the week — and *Monthly mileage 72 / 130 this week* is the bug that came
 * of labelling one with the other. Each caller passes the window its figure was measured over.
 *
 * ## The short form
 *
 * `0/3` — no unit, no window — for Today's pill, which is a fixed-width chip whose `title` already
 * names the period. A second output of this function, not a second author. A reading's short form
 * is its full form; abstinence has none, and the pill prints the verdict word instead.
 */

/** The window a figure was measured over. A review's can be a quarter; a habit's cannot. */
export type FigureWindow = 'day' | 'week' | 'month' | 'quarter';

/**
 * The window, said: *today*, *this week*. One spelling for the figure's *of 14 this week* and the
 * verdict's *not read this week* (`verdictWord`), so a row cannot name its window two ways.
 */
export const WINDOW_WORDS: Record<FigureWindow, string> = {
  day: 'today',
  week: 'this week',
  month: 'this month',
  quarter: 'this quarter',
};

/** What a payload has to carry for its figure to be said. Every habit payload carries these. */
export interface FigureFacts {
  actual: number;
  target: number;
  unit?: string | null;
  polarity?: HabitPolarity;
  isAbstinence: boolean;
  isReading: boolean;
  lastReading?: number | null;
  lastReadOn?: string | null;
}

export interface FigureOptions {
  /** The window the figure answers for — the caller's to say, see above. */
  window: FigureWindow;
  /** The account's calendar day the figure is judged at: a reading taken on it needs no day. */
  asOf: string;
  /**
   * The account's today — never the browser's. When `asOf` is today a reading's day is said as its
   * distance from it (`8 days ago`); when it is not, as the calendar day (`on 5 Oct`).
   */
  today: string;
  /** `0/3`, for a pill. */
  short?: boolean;
  /**
   * The target is the calendar's arithmetic on the habit's own (Reflection's `scaledTarget`), and
   * is said as `≈80` so nobody reads 79.71 as a figure the habit set.
   */
  approxTarget?: boolean;
}

/**
 * The figure, or null where there is none to state — abstinence in the short form, whose pill
 * carries the verdict word instead.
 */
export function habitFigure(h: FigureFacts, opts: FigureOptions): string | null {
  const unit = h.unit ?? null;

  if (h.isReading) {
    const on = h.lastReadOn ?? null;
    const reading = h.lastReading ?? null;
    if (on === null) return 'no reading yet';
    if (reading === null) return `last read ${readingDay(on, opts, '') ?? 'today'}`;
    const day = readingDay(on, opts, 'on ');
    return day === null ? amount(reading, unit) : `${amount(reading, unit)} ${day}`;
  }

  const window = WINDOW_WORDS[opts.window];
  if (h.isAbstinence) return opts.short ? null : `${amount(h.actual, unit)} ${window}`;

  const target = opts.approxTarget ? `≈${Math.round(h.target)}` : amount(h.target, null);
  if (opts.short) return `${amount(h.actual, null)}/${target}`;
  const allowed = h.polarity === 'negative' ? ' allowed' : '';
  return `${amount(h.actual, unit)} of ${target}${allowed} ${window}`;
}

/**
 * The number inside a Habits card's 64px ring (`01a08096-e961`), or null where the ring should say
 * none and leave the arc and its colour to speak.
 *
 * A number only where it is the arc's numerator: a habit you are *doing*, whose arc is
 * `actual / target`. There it is `actual` through `amount()`, the same figure the line beside the
 * ring opens with — that line is the ring's accessible equivalent, the ring being `aria-hidden`, so
 * the two say one number. It used to be rounded to one decimal of its own, and a week of 12.46 km
 * read *12.5* in the ring beside *12.46 km of 30* in the line.
 *
 * Every limit says nothing, each for its own reason, and none of them for want of room:
 *
 * - **a reading** — the ring printed `actual`, so a period nobody read was a **0** inside the ring
 *   (`01a154cc-c8ac` again) and a weekly `mean` printed the week's 80 beside a line stating the
 *   latest 78. `lastReading` would mend both and break the rule above: a reading is said with its
 *   day or it reads as this morning's, and *13.48* in a ring beside *13.48 % 8 days ago* is the
 *   day dropped. The arc is not a fraction of the reading either — it is `band.position`.
 * - **a tallied limit** — `0` units of 2 allowed is the limit kept, and the ring drew it as the same
 *   bare 0 over the same empty arc as Barbell tonnage with nothing lifted. *Allowed* is the word that
 *   tells the two apart, and it is in the line.
 * - **abstinence** — the ring printed the clean-day count: a number of days in a row of rings that
 *   otherwise hold each habit's unit, and one the line already says with the predicate it needs
 *   (*20 clean days including today*, beside a run of 19).
 *
 * What the arc means on a limit, and whether abstinence wants a different shape, is not this
 * function's: the architect left both to a persona re-probe of the current card.
 */
export function ringFigure(h: FigureFacts & { polarity: HabitPolarity }): string | null {
  if (h.isReading || h.isAbstinence || h.polarity === 'negative') return null;
  return amount(h.actual, null);
}

/** How long ago, in the ladder `386d2b0` set: yesterday, 2–13 days, 2–8 weeks, then months. */
function agoLabel(days: number): string {
  if (days === 1) return 'yesterday';
  if (days < 14) return `${days} days ago`;
  if (days < 60) return `${Math.floor(days / 7)} weeks ago`;
  return `${Math.floor(days / 30)} months ago`;
}

/**
 * The words for the day a reading was taken, or null when it was taken on the day judged and
 * needs none — see *The reading's day* above. `on` precedes a calendar day after a figure
 * (`13.2 % on 5 Oct`) and not after *last read* (`last read 3 Jun`).
 */
function readingDay(
  on: string,
  opts: { asOf: string; today: string },
  before: 'on ' | '',
): string | null {
  if (on === opts.asOf) return null;
  const days = daysBetween(on, opts.today);
  // A reading after the day judged is not one the service reports; say its day rather than guess.
  return opts.asOf === opts.today && days > 0 ? agoLabel(days) : `${before}${shortDay(on)}`;
}

/**
 * The day a review's figures are judged at: the last day it has read to — its end, or the
 * account's today while it is still running. The same clamp the service reads its rows to, so a
 * reading taken on that day is stated without one and any earlier reading with its own.
 */
export const reviewAsOf = (review: { end: string; today: string }): string =>
  review.end < review.today ? review.end : review.today;
